import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useAccount } from '@/domains/shared/components/AccountAccess/AccountContext';
import InlineLoader from '@/domains/shared/components/InlineLoader';
import { useAuthContext } from '../authContext';
import {
  getPendingRedirect,
  isPendingRedirectConsumed,
  markPendingRedirectConsumed,
  setConsentReturn,
  setPendingRedirect,
  takeConsentReturn,
  toConsentReturnRoute,
  toParkableRoute,
} from '../state/pendingRedirect';

type UserType = 'public' | 'lead' | 'account';

type RedirectPaths = {
  public?: string;
  lead?: string;
  account?: string;
};

const redirectPaths: Required<RedirectPaths> = {
  public: '/hive/accounts/login',
  lead: '/hive/accounts/get-started',
  account: '/hive/beehive/summary',
};

export const withRouteGuard = <P extends Record<string, unknown>>(
  WrappedComponent: React.ComponentType<P>,
  allowedUserTypes: UserType[],
): React.FC<P> => {
  const RouteGuardedComponent: React.FC<P> = (props) => {
    const [loading, setLoading] = useState(true);
    const router = useRouter();
    const { authReady, isLoggedIn } = useAuthContext();

    const { userType, isLoading: isLoadingUserType } = useAccount();

    useEffect(() => {
      if (!authReady) return;

      if (!isLoggedIn) {
        if (!allowedUserTypes.includes('public')) {
          const consentReturn = toConsentReturnRoute(router.asPath);
          const attemptedRoute = toParkableRoute(router.asPath);
          if (consentReturn) setConsentReturn(consentReturn);
          else if (attemptedRoute) setPendingRedirect(attemptedRoute);

          router.replace(redirectPaths.public);
        }
        setLoading(false);
        return;
      }

      if (isLoadingUserType) return;

      const resolvedUserType = userType ?? 'public';

      if (!allowedUserTypes.includes(resolvedUserType)) {
        // A partner waiting on consent comes before any other parked route.
        const consentReturn =
          resolvedUserType === 'account' ? takeConsentReturn() : null;

        const pendingRedirect = getPendingRedirect();
        const alreadyOnPendingRedirect =
          pendingRedirect === router.asPath ||
          pendingRedirect === router.pathname;

        const sendToPendingRedirect =
          !consentReturn &&
          !!pendingRedirect &&
          !isPendingRedirectConsumed() &&
          !alreadyOnPendingRedirect &&
          resolvedUserType === 'account';

        if (sendToPendingRedirect) markPendingRedirectConsumed();

        router.replace(
          consentReturn ??
            (sendToPendingRedirect
              ? pendingRedirect
              : redirectPaths[resolvedUserType]),
        );
      }

      setLoading(false);
    }, [authReady, isLoggedIn, isLoadingUserType, userType, allowedUserTypes]);

    if (loading) {
      return (
        <div className="fixed inset-0 flex justify-center items-center bg-background">
          <InlineLoader />
        </div>
      );
    }

    return <WrappedComponent {...(props as P)} />;
  };

  return RouteGuardedComponent;
};

// Route guard for public-only pages (e.g., login, signup landing)
export const withPublicOnly = <P extends Record<string, unknown>>(
  WrappedComponent: React.ComponentType<P>,
) => {
  return withRouteGuard(WrappedComponent, ['public']);
};

// Route guard for lead-only pages (signup flow)
export const withLeadOnly = <P extends Record<string, unknown>>(
  WrappedComponent: React.ComponentType<P>,
) => {
  return withRouteGuard(WrappedComponent, ['lead']);
};

// Route guard for account-only pages (beehive, account management)
export const withAccountOnly = <P extends Record<string, unknown>>(
  WrappedComponent: React.ComponentType<P>,
) => {
  return withRouteGuard(WrappedComponent, ['account']);
};

// Route guard for authenticated pages (any logged-in user: lead or account, e.g. add-transfers)
export const withAuthenticatedOnly = <P extends Record<string, unknown>>(
  WrappedComponent: React.ComponentType<P>,
) => {
  return withRouteGuard(WrappedComponent, ['lead', 'account']);
};
